import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { TextField } from '@/components/ui/text-field';
import { FORM_ERROR, errorMessage } from '@/features/auth/use-auth-error';
import { ZIP_PATTERN } from '@/lib/validation';

import { InfoDialog, type InfoTopic } from '../info-dialogs';

import { ChangePasswordDialog } from './change-password-dialog';
import type { StepProps } from './step-props';
import { useSaveStep } from './use-save-step';

interface ProfileForm {
  firstName: string;
  lastName: string;
  zipCode: string;
}

export function ProfileStep({ record, onNext }: StepProps) {
  const [info, setInfo] = useState<InfoTopic>();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const save = useSaveStep('profile', onNext);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted, isDirty },
  } = useForm<ProfileForm>({
    defaultValues: {
      firstName: record.firstName,
      lastName: record.lastName,
      zipCode: record.zipcode ?? '',
    },
  });

  const submit = (v: ProfileForm) => {
    if (!isDirty) {
      onNext({ kind: 'step', step: 'work_type' });
      return;
    }
    save.mutate({ firstName: v.firstName.trim(), lastName: v.lastName.trim(), zipCode: v.zipCode });
  };

  const hasFieldErrors = isSubmitted && Object.keys(errors).length > 0;

  return (
    <div className="stack">
      <h1>Edit your account</h1>
      <hr className="divider" />
      {save.isError && <Alert>{errorMessage(save.error)}</Alert>}
      {!save.isError && hasFieldErrors && <Alert>{FORM_ERROR}</Alert>}

      <form className="stack" noValidate onSubmit={(e) => void handleSubmit(submit)(e)}>
        <TextField
          id="step1-firstName"
          label="First Name"
          autoComplete="given-name"
          error={errors.firstName?.message}
          {...register('firstName', { required: 'Please provide your first name' })}
        />
        <TextField
          id="step1-lastName"
          label="Last Name"
          autoComplete="family-name"
          error={errors.lastName?.message}
          {...register('lastName', { required: 'Please provide your last name' })}
        />
        <div className="field">
          <div className="field__label">
            Email Address{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setInfo('editEmail');
              }}
            >
              Why can&apos;t I edit this?
            </button>
          </div>
          <div className="readonly">{record.email}</div>
        </div>
        <TextField
          id="step1-zipCode"
          label="Zip Code"
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={5}
          error={errors.zipCode?.message}
          {...register('zipCode', {
            required: 'Please provide a valid Zip Code',
            pattern: { value: ZIP_PATTERN, message: 'Please provide a valid Zip Code' },
          })}
        />
        <div>
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setPasswordOpen(true);
            }}
          >
            Change Password
          </button>
        </div>
        <Button type="submit" busy={save.isPending}>
          Save &amp; continue
        </Button>
      </form>

      <InfoDialog
        topic={info}
        onClose={() => {
          setInfo(undefined);
        }}
      />
      <ChangePasswordDialog
        open={passwordOpen}
        onClose={() => {
          setPasswordOpen(false);
        }}
      />
    </div>
  );
}
